/**
 * useRewardsSystem - Hook pour gérer le système de récompenses
 *
 * Gère:
 * - Les récompenses débloquées
 * - L'historique d'utilisation
 * - Les récompenses en cours
 * - Les coins/points de récompense
 */

import { useState, useCallback, useMemo } from 'react';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { rewardsService, REWARD_CATEGORIES } from '../services/rewardsService';
import { haptic } from '../../../shared/utils/haptics';

export function useRewardsSystem(gameData = {}) {
  const { totalXP = 0 } = gameData;

  // État persistant des récompenses
  const [rewardsData, setRewardsData] = useLocalStorage('adhd-planner-rewards', {
    rewardCoins: 0, // Points de récompense (gagnés en complétant des tâches)
    unlockedRewards: [], // IDs des récompenses permanentes débloquées
    usageHistory: [], // Historique d'utilisation
    favorites: [], // Récompenses favorites
    lastUsed: null, // Dernière récompense utilisée
    streakBonus: 0, // Bonus de coins pour les séries
    dailyFreeReward: null, // Récompense gratuite du jour
    lastDailyRewardDate: null,
  });

  // État de l'interface
  const [activeReward, setActiveReward] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showRewardsPanel, setShowRewardsPanel] = useState(false);

  // Coins disponibles (combinaison XP + coins bonus)
  const availableCoins = useMemo(() => {
    // On peut utiliser soit les XP soit les coins de récompense
    return Math.floor(totalXP * 0.5) + rewardsData.rewardCoins;
  }, [totalXP, rewardsData.rewardCoins]);

  // Ajouter des coins (appelé quand on complète des tâches)
  const addCoins = useCallback((amount, source = 'task') => {
    setRewardsData(prev => ({
      ...prev,
      rewardCoins: prev.rewardCoins + amount,
    }));
    return amount;
  }, [setRewardsData]);

  // Bonus de coins pour les séries
  const addStreakBonus = useCallback((streakDays) => {
    const bonus = Math.min(streakDays * 5, 50); // Max 50 coins bonus
    setRewardsData(prev => ({
      ...prev,
      rewardCoins: prev.rewardCoins + bonus,
      streakBonus: bonus,
    }));
    return bonus;
  }, [setRewardsData]);

  // Vérifier si une récompense est débloquée
  const isUnlocked = useCallback((rewardId) => {
    return rewardsData.unlockedRewards.includes(rewardId);
  }, [rewardsData.unlockedRewards]);

  // Vérifier si on peut utiliser une récompense
  const canUseReward = useCallback((rewardId) => {
    const reward = rewardsService.getById(rewardId);
    if (!reward) return false;

    // Les récompenses permanentes doivent être débloquées
    if (reward.type === 'unlock' && !isUnlocked(rewardId)) {
      return availableCoins >= reward.cost;
    }

    // Les autres récompenses coûtent des coins à chaque utilisation
    return availableCoins >= reward.cost;
  }, [availableCoins, isUnlocked]);

  // Débloquer une récompense permanente
  const unlockReward = useCallback((rewardId) => {
    const reward = rewardsService.getById(rewardId);
    if (!reward) return false;

    if (availableCoins < reward.cost) {
      return false;
    }

    setRewardsData(prev => ({
      ...prev,
      rewardCoins: prev.rewardCoins - reward.cost,
      unlockedRewards: [...prev.unlockedRewards, rewardId],
    }));

    haptic.success();
    return true;
  }, [availableCoins, setRewardsData]);

  // Utiliser une récompense
  const useReward = useCallback((rewardId) => {
    const reward = rewardsService.getById(rewardId);
    if (!reward) return { success: false, error: 'Récompense introuvable' };

    // Vérifier les coins (sauf pour les récompenses déjà débloquées)
    const isPermanent = reward.type === 'unlock';
    const alreadyUnlocked = isUnlocked(rewardId);

    if (!isPermanent && !alreadyUnlocked) {
      if (availableCoins < reward.cost) {
        return {
          success: false,
          error: `Il te manque ${reward.cost - availableCoins} coins`,
        };
      }
    }

    // Déduire les coins (sauf si déjà débloqué)
    if (!isPermanent && !alreadyUnlocked) {
      setRewardsData(prev => ({
        ...prev,
        rewardCoins: Math.max(0, prev.rewardCoins - Math.floor(reward.cost * 0.5)), // Demi-coût pour réutilisation
        lastUsed: rewardId,
        usageHistory: [
          { rewardId, timestamp: Date.now() },
          ...prev.usageHistory.slice(0, 49),
        ],
      }));
    } else {
      setRewardsData(prev => ({
        ...prev,
        lastUsed: rewardId,
        usageHistory: [
          { rewardId, timestamp: Date.now() },
          ...prev.usageHistory.slice(0, 49),
        ],
      }));
    }

    setActiveReward(reward);
    setIsPlaying(true);
    haptic.success();

    return { success: true, reward };
  }, [availableCoins, isUnlocked, setRewardsData]);

  // Arrêter la récompense en cours
  const stopReward = useCallback(() => {
    setActiveReward(null);
    setIsPlaying(false);
  }, []);

  // Ajouter/retirer des favoris
  const toggleFavorite = useCallback((rewardId) => {
    setRewardsData(prev => {
      const isFav = prev.favorites.includes(rewardId);
      return {
        ...prev,
        favorites: isFav
          ? prev.favorites.filter(id => id !== rewardId)
          : [...prev.favorites, rewardId],
      };
    });
  }, [setRewardsData]);

  // Vérifier si c'est un favori
  const isFavorite = useCallback((rewardId) => {
    return rewardsData.favorites.includes(rewardId);
  }, [rewardsData.favorites]);

  // Obtenir les récompenses recommandées
  const getRecommendations = useCallback(() => {
    return rewardsService.getRecommended();
  }, []);

  // Obtenir les favoris
  const getFavorites = useCallback(() => {
    return rewardsData.favorites
      .map(id => rewardsService.getById(id))
      .filter(Boolean);
  }, [rewardsData.favorites]);

  // Obtenir l'historique récent
  const getRecentlyUsed = useCallback(() => {
    const seen = new Set();
    return rewardsData.usageHistory
      .filter(entry => {
        if (seen.has(entry.rewardId)) return false;
        seen.add(entry.rewardId);
        return true;
      })
      .slice(0, 5)
      .map(entry => rewardsService.getById(entry.rewardId))
      .filter(Boolean);
  }, [rewardsData.usageHistory]);

  // Vérifier et attribuer la récompense gratuite quotidienne
  const checkDailyFreeReward = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];

    if (rewardsData.lastDailyRewardDate !== today) {
      // Choisir une récompense gratuite aléatoire parmi les moins chères
      const cheapRewards = rewardsService.getAll()
        .filter(r => r.cost <= 30)
        .sort(() => Math.random() - 0.5);

      const freeReward = cheapRewards[0];

      setRewardsData(prev => ({
        ...prev,
        dailyFreeReward: freeReward?.id || null,
        lastDailyRewardDate: today,
      }));

      return freeReward;
    }

    return rewardsData.dailyFreeReward
      ? rewardsService.getById(rewardsData.dailyFreeReward)
      : null;
  }, [rewardsData.lastDailyRewardDate, rewardsData.dailyFreeReward, setRewardsData]);

  // Utiliser la récompense gratuite du jour
  const useDailyFreeReward = useCallback(() => {
    const freeReward = rewardsData.dailyFreeReward;
    if (!freeReward) return { success: false, error: 'Pas de récompense gratuite disponible' };

    const reward = rewardsService.getById(freeReward);
    if (!reward) return { success: false, error: 'Récompense introuvable' };

    setRewardsData(prev => ({
      ...prev,
      dailyFreeReward: null, // Consommée
      usageHistory: [
        { rewardId: freeReward, timestamp: Date.now(), free: true },
        ...prev.usageHistory.slice(0, 49),
      ],
    }));

    setActiveReward(reward);
    setIsPlaying(true);
    haptic.success();

    return { success: true, reward };
  }, [rewardsData.dailyFreeReward, setRewardsData]);

  // Stats des récompenses
  const stats = useMemo(() => ({
    totalUnlocked: rewardsData.unlockedRewards.length,
    totalUsed: rewardsData.usageHistory.length,
    favoritesCount: rewardsData.favorites.length,
    availableCoins,
  }), [rewardsData, availableCoins]);

  return {
    // État
    availableCoins,
    activeReward,
    isPlaying,
    showRewardsPanel,

    // Actions
    addCoins,
    addStreakBonus,
    useReward,
    stopReward,
    unlockReward,
    toggleFavorite,
    setShowRewardsPanel,

    // Vérifications
    isUnlocked,
    isFavorite,
    canUseReward,

    // Listes
    getRecommendations,
    getFavorites,
    getRecentlyUsed,
    checkDailyFreeReward,
    useDailyFreeReward,

    // Données
    stats,
    categories: REWARD_CATEGORIES,
    catalog: rewardsService.catalog,

    // Utilitaires
    getRewardById: rewardsService.getById.bind(rewardsService),
    getByCategory: rewardsService.getByCategory.bind(rewardsService),
    getAllRewards: rewardsService.getAll.bind(rewardsService),
  };
}

export default useRewardsSystem;
